import {
  COVERAGE_TIMELINE_AXIS_STROKE_WIDTH,
  COVERAGE_TIMELINE_CHART_HEIGHT,
  COVERAGE_TIMELINE_CHART_LIFT,
  COVERAGE_TIMELINE_MARGIN_BOTTOM,
  COVERAGE_TIMELINE_MARGIN_LEFT,
  COVERAGE_TIMELINE_MARGIN_RIGHT,
  COVERAGE_TIMELINE_MARGIN_TOP,
  COVERAGE_TIMELINE_X_AXIS_HEIGHT,
  COVERAGE_TIMELINE_Y_AXIS_MAX,
  COVERAGE_TIMELINE_Y_AXIS_TICKS,
  COVERAGE_TIMELINE_Y_AXIS_WIDTH,
  COVERAGE_TIMELINE_Y_DOMAIN_PADDING_BOTTOM_RATIO,
  COVERAGE_TIMELINE_Y_DOMAIN_PADDING_TOP_RATIO,
} from '@/features/codeCoverage/pages/coverageRunsPage.constants';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks/useQualityDateRange';
import {
  MiniMetricChartHeader,
  type MiniMetricChartSummary,
} from '@/features/shared/components/chartPanels/MiniMetricChartHeader';
import { MiniMetricLineChart } from '@/features/shared/components/chartPanels/MiniMetricLineChart';
import { StatusChartPanel } from '@/features/shared/components/chartPanels/StatusChartPanel';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getErrorCopy } from '@/shared/copy/errors';
import { s } from '@/theme/tokens';
import { Box } from '@/ui';
import { CHART_EMPTY_STATE_FILTER_HINT, ChartEmptyState } from '@/ui/composites';
import { formatChartTooltipDateLabel } from '@/ui/primitives/chartDateLabel';
import { CoverageTimelineChartSkeleton } from './CoverageTimelineChartSkeleton';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { ReactElement, ReactNode } from 'react';

type CoverageTimelineChartPanelProps = {
  title: string;
  data: Array<{ label: string; measuredValue: number | null; carryForwardValue: number }>;
  seriesLabel: string;
  color: string;
  summary?: MiniMetricChartSummary | null;
  loading?: boolean;
  showErrorState?: boolean;
  emptyTitle?: string;
};

const DEFAULT_DATE_QUICK_FILTER: DateQuickFilter = 'latest';
const DEFAULT_EMPTY_TITLE = formatRangeWindowNoDataText(
  getEmptyStateMessage('entity.noneFound', { entity: 'coverageRun' }),
  '7d',
  false,
);

type CoverageTimelineChartBodyProps = {
  children: ReactNode;
  lift?: boolean;
  centerContent?: boolean;
};

const CoverageTimelineChartBody = ({
  children,
  lift = false,
  centerContent = false,
}: CoverageTimelineChartBodyProps): ReactElement => (
  <Box
    sx={{
      height: `${COVERAGE_TIMELINE_CHART_HEIGHT}px`,
      width: '100%',
      mt: lift ? COVERAGE_TIMELINE_CHART_LIFT : undefined,
      display: centerContent ? 'flex' : undefined,
      alignItems: centerContent ? 'center' : undefined,
      justifyContent: centerContent ? 'center' : undefined,
    }}
  >
    {children}
  </Box>
);

export const CoverageTimelineChartPanel = ({
  title,
  data,
  seriesLabel: _seriesLabel,
  color,
  summary = null,
  loading = false,
  showErrorState = false,
  emptyTitle = DEFAULT_EMPTY_TITLE,
}: CoverageTimelineChartPanelProps): ReactElement => {
  const showLoadingState = loading;
  return (
    <StatusChartPanel
      title={title}
      chartHeight={COVERAGE_TIMELINE_CHART_HEIGHT}
      searchAriaLabel={`Search ${title.toLowerCase()}`}
      showInlineFilters={false}
      showSearch={false}
      searchValue=""
      onDebouncedSearchChange={() => undefined}
      filterStatuses={[]}
      statusOptions={[]}
      onFilterStatusesChange={() => undefined}
      filterTags={[]}
      onFilterTagsChange={() => undefined}
      availableTags={[]}
      dateQuickFilter={DEFAULT_DATE_QUICK_FILTER}
      onDateQuickFilterChange={() => undefined}
      fromDate={null}
      toDate={null}
      onFromDateChange={() => undefined}
      onToDateChange={() => undefined}
      panelPadding="md"
      panelContentGap="none"
      headerOverride={
        <Box
          sx={{
            px: s('md'),
            minHeight: '28px',
            boxSizing: 'border-box',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <MiniMetricChartHeader title={title} summary={summary} valueFontSize="15px" />
        </Box>
      }
      emptyTitle={emptyTitle}
      emptyBody={CHART_EMPTY_STATE_FILTER_HINT}
    >
      {showLoadingState ? (
        <CoverageTimelineChartBody lift>
          <CoverageTimelineChartSkeleton height={COVERAGE_TIMELINE_CHART_HEIGHT} />
        </CoverageTimelineChartBody>
      ) : showErrorState ? (
        <CoverageTimelineChartBody centerContent>
          <ChartEmptyState title={getErrorCopy('banner.chartLoadFailed')} variant="metricCard" />
        </CoverageTimelineChartBody>
      ) : data.length > 0 ? (
        <CoverageTimelineChartBody lift>
          <MiniMetricLineChart
            data={data}
            metricKey="value"
            color={color}
            height={COVERAGE_TIMELINE_CHART_HEIGHT}
            marginLeft={COVERAGE_TIMELINE_MARGIN_LEFT}
            marginRight={COVERAGE_TIMELINE_MARGIN_RIGHT}
            marginTop={COVERAGE_TIMELINE_MARGIN_TOP}
            marginBottom={COVERAGE_TIMELINE_MARGIN_BOTTOM}
            xAxisHeight={COVERAGE_TIMELINE_X_AXIS_HEIGHT}
            yAxisWidth={COVERAGE_TIMELINE_Y_AXIS_WIDTH}
            yAxisTickNumber={3}
            yAxisTickInterval={COVERAGE_TIMELINE_Y_AXIS_TICKS}
            axisStrokeWidth={COVERAGE_TIMELINE_AXIS_STROKE_WIDTH}
            tooltipLabelFormatter={formatChartTooltipDateLabel}
            showAllXAxisLabels
            showHorizontalGrid
            hideXAxisLine={false}
            yAxisMin={0}
            yAxisMax={COVERAGE_TIMELINE_Y_AXIS_MAX}
            yDomainPaddingTopRatio={COVERAGE_TIMELINE_Y_DOMAIN_PADDING_TOP_RATIO}
            yDomainPaddingBottomRatio={COVERAGE_TIMELINE_Y_DOMAIN_PADDING_BOTTOM_RATIO}
            showPlotAreaBackground
            showPlotAreaFrame
          />
        </CoverageTimelineChartBody>
      ) : (
        <CoverageTimelineChartBody centerContent>
          <ChartEmptyState title={emptyTitle} body={CHART_EMPTY_STATE_FILTER_HINT} />
        </CoverageTimelineChartBody>
      )}
    </StatusChartPanel>
  );
};
